import { ArrowForward } from '@mui/icons-material';
import { Box, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  getChipColorForLetter,
  getConversationStatusChipColor,
} from '@/modules/common/application/ui';
import { Chip } from '@/modules/common/components';
import { ChipColorVariants, MessageStatus } from '@/modules/common/types';

import { Labels } from '../types/article';
import { ConversationStatus } from '../types/conversation';

interface ChatCardProps {
  title: string;
  date: string;
  labels: ConversationStatus | string[] | null;
  status: MessageStatus | null;
  onClick: () => void;
}

export const ChatCard = ({ title, date, labels, status, onClick }: ChatCardProps) => {
  const { t } = useTranslation();

  // Determine what content to display based on message status
  const displayContent =
    status === MessageStatus.IN_PROGRESS
      ? t('modules.chat.chatConversations.processing')
      : status === MessageStatus.FAILED
        ? t('modules.mavue2.chat.responseFailed')
        : title?.replace(/[*]/g, '');

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
        position: 'relative',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        bgcolor: 'white',
      }}
      onClick={onClick}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
          <Box display="flex" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {new Date(date).toLocaleDateString('de-DE')}
            </Typography>
          </Box>

          {labels && (
            <>
              {Array.isArray(labels) ? (
                labels?.map((label) => (
                  <Box key={label} display="flex" alignItems="center">
                    <Chip
                      size="small"
                      colorVariant={
                        label === Labels.NEW_FEATURE
                          ? ChipColorVariants.LIGHT_GREEN
                          : label === Labels.FEATURE_UPDATE
                            ? ChipColorVariants.WATER
                            : getChipColorForLetter(label)
                      }
                      styles={{
                        height: 16,
                        padding: '0 6px',
                        lineHeight: 1,
                      }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                        {label}
                      </Typography>
                    </Chip>
                  </Box>
                ))
              ) : (
                <Chip
                  size="small"
                  colorVariant={getConversationStatusChipColor(labels)}
                  styles={{
                    height: 16,
                    padding: '0 6px',
                    lineHeight: 1,
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                    {t(`modules.chat.conversationStatus.${labels}`)}
                  </Typography>
                </Chip>
              )}
            </>
          )}
        </Box>
      </Box>
      <Typography variant="subtitle2" noWrap pr={3}>
        {displayContent}
      </Typography>
      <Box
        sx={{
          position: 'absolute',
          right: 6,
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <ArrowForward fontSize="small" color="primary" />
      </Box>
    </Paper>
  );
};
